import { CircularProgress, Checkbox, FormControlLabel, FormGroup } from '@material-ui/core';
import { AccessLevels } from '../../config/access.levels';
import Button from '@material-ui/core/Button';
import { Search, GetAppRounded, Refresh } from '@material-ui/icons';
import { debounce } from 'lodash';
import { generateReport } from '../../redux/actions/reports.actions';
import { FilterOffice } from './FilterZoneOffice';
import { FilterWorkshop } from 'types/api/responses/workshop';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import { Pager } from 'types/api/request';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { workshopValues } from 'utils';
import React, { useState, KeyboardEvent, ChangeEvent, useCallback, useEffect } from 'react';
import { Styles, WorkshopTypesList } from '../Tools';
import { useTypedSelector, useThunkDispatch } from '../../hooks';
import { workshopAll } from 'redux/actions/workshop.actions';
import { FilterReportMap } from './FilterReportMap';
import { FilterDate } from './FilterDate';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useLocation } from 'react-router-dom';

const params: Pager = {
  size: 10,
  page: 1,
} as const;

const rex = /\d{7}/;

export const FiltersWorshop = () => {
  const { filter, error, isLoading, branchOffices } = useTypedSelector((z) => z.workshop);
  const myRole = useTypedSelector((s) => s.session.user.role.accessLevel);
  const dispatch = useThunkDispatch();
  const [fState, setFilter] = useState<FilterWorkshop>(filter);
  const [validation, setValidation] = useState('');
  const [dateError, setDateError] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const classes = Styles();
  const isCoordinator = myRole !== AccessLevels.HelpDesk;
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  // Sincronizar el estado local con el filtro de Redux solo si viene desde Dashboard (bind=true)
  const searchParams = new URLSearchParams(location.search);
  const shouldBind = searchParams.get('bind') === 'true';

  useEffect(() => {
    if (shouldBind && (filter.Status || filter.ZoneId || filter.OfficeId)) {
      setFilter(filter);
    }

    if (!shouldBind && (fState.Status || fState.ZoneId || fState.OfficeId)) {
      const cleanFilter = {
        NameContains: null,
        Status: null,
        ZoneId: null,
        OfficeId: null,
        DaysBefore: null,
        Account: null,
        StartDate: null,
        EndDate: null,
        Active: null,
      };
      setFilter(cleanFilter);
      dispatch(workshopAll({ params, filterN: cleanFilter }));
    }
  }, [shouldBind && filter]);

  const reportWorkshop = async (filter: FilterWorkshop) => {
    setDateError('');
    setLoading(true);
    try {
      await generateReport(filter);
    } finally {
      setLoading(false);
    }
  };

  const onChangeFilter = <T extends keyof FilterWorkshop>(
    key: T,
    value: FilterWorkshop[T],
  ) => {
    setFilter({ ...fState, [key]: value });
  };

  const handleSearch = (s: FilterWorkshop): void => {
    if (
      !s.Account &&
      !s.NameContains &&
      !s.Status &&
      !s.ZoneId &&
      !s.OfficeId &&
      !s.DaysBefore &&
      !s.StartDate &&
      !s.EndDate &&
      !s.Active
    ) {
      const filterN = {
        NameContains: null,
        Status: null,
        ZoneId: null,
        OfficeId: null,
        DaysBefore: null,
        Account: null,
        StartDate: null,
        EndDate: null,
        Active: null,
      };
      setFilter(filterN);
      dispatch(workshopAll({ params, filterN }));
      return;
    }

    if (s.Account) {
      const valid = rex.test(s.Account);
      if (!valid) {
        setValidation('Debe tener exactamente 7 digitos');
        return;
      }
    }

    setValidation('');
    dispatch(workshopAll({ params, filterN: s }));
  };

  const handleResetSearch = () => {
    const resetFilter = {
      ...fState,
      NameContains: null,
      Status: null,
      ZoneId: null,
      OfficeId: null,
      DaysBefore: null,
      Account: null,
      StartDate: null,
      EndDate: null,
      Active: null,
    };
    setFilter(resetFilter);
    dispatch(
      workshopAll({
        filterN: resetFilter,
        params: params,
      }),
    );
  };

  const debouncedAccount = useCallback(debounce(handleSearch, 500), [dispatch]);

  const onAccount = (evt: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter('Account', evt.target.value);
    debouncedAccount.cancel();
    debouncedAccount({ ...fState, Account: evt.target.value });
  };

  const onName = (evt: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter('NameContains', evt.target.value);
    debouncedAccount.cancel();
    debouncedAccount({ ...fState, NameContains: evt.target.value });
  };

  const onKeyPress = (evt: KeyboardEvent<unknown>) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      debouncedAccount.cancel();
      handleSearch(fState);
    }
  };

  const debouncedDate = useCallback(debounce(handleSearch, 500), []);

  const handleStartDateChange = (value: string) => {
    setDateError('');
    const updatedFilter = { ...fState, StartDate: value };
    setFilter(updatedFilter);
    debouncedDate(updatedFilter);
  };

  const handleEndDateChange = (value: string) => {
    setDateError('');
    const updatedFilter = { ...fState, EndDate: value };
    setFilter(updatedFilter);
    debouncedDate(updatedFilter);
  };

  // Validación para bloquear acciones cuando hay errores de fecha
  const hasDateValidationError = () => {
    if ((fState.StartDate && !fState.EndDate) || (!fState.StartDate && fState.EndDate)) {
      return true;
    }
    if (fState.StartDate && fState.EndDate) {
      return new Date(fState.StartDate) > new Date(fState.EndDate);
    }
    return false;
  };

  const handleOfficeChange = (ZoneId: number | null, OfficeId: string | null) => {
    const filterN = { ...fState, ZoneId, OfficeId };
    setFilter(filterN);
    dispatch(workshopAll({ params, filterN }));
  };

  return (
    <Paper className={`${classes.paper} ${classes.flexWrap}`}>
      <div className={classes.flexWrap}>
        <TextField
          id="Account"
          label="Cuenta"
          error={Boolean(error) || Boolean(validation)}
          helperText={validation || error?.message}
          value={fState.Account || ''}
          onChange={onAccount}
          onKeyPress={onKeyPress}
          variant="outlined"
          inputProps={{ maxLength: workshopValues.clientAccount, readOnly: isLoading }}
        />
        <TextField
          id="name"
          label="Nombre Comercial"
          inputProps={{ maxLength: workshopValues.name, readOnly: isLoading }}
          value={fState.NameContains || ''}
          onChange={onName}
          onKeyPress={onKeyPress}
          variant="outlined"
        />
        <FilterDate
          StartDate={fState.StartDate || ''}
          EndDate={fState.EndDate || ''}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
        {dateError && <span style={{ color: 'red' }}>{dateError}</span>}
        <FormControl style={{ marginRight: '10px' }}>
          <InputLabel id="state-id-label">Perfil</InputLabel>
          <Select
            labelId="state-id-label"
            id="state-id"
            value={fState.Status ?? '0'}
            onChange={(evt) =>
              onChangeFilter(
                'Status',
                (evt.target.value as string) === '0' ? null : (evt.target.value as string),
              )
            }
          >
            <MenuItem value="0">Sin Filtrar</MenuItem>
            {[...WorkshopTypesList]
              .sort((a, b) => {
                // Definir el orden de los estados
                const statusOrder: { [key: string]: number } = {
                  Created: 0,
                  Censused: 1,
                  ProfiledIncompleted: 2,
                  VerifiedIncompleted: 3,
                  TaProfiledIncompleted: 4,
                  TaVerifiedIncompleted: 5,
                  Profiled: 6,
                  TaProfiled: 7,
                  ProfiledCompleted: 8,
                  TaProfiledCompleted: 9,
                };

                const orderA = statusOrder[a.number] ?? 999;
                const orderB = statusOrder[b.number] ?? 999;

                return orderA - orderB;
              })
              .map((item) => {
                return (
                  <MenuItem value={item.number} key={item.number}>
                    <Paper
                      className={classes.paper}
                      style={{ backgroundColor: item.color, color: item.fix }}
                    >
                      {item.type}
                    </Paper>
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
        <FilterOffice
          offices={branchOffices.offices}
          onChange={handleOfficeChange}
          zoneId={fState.ZoneId || 0}
          officeId={fState.OfficeId}
        />
        <FormControl style={{ marginRight: '10px' }}>
          <InputLabel id="days-controlled-open-select-label">Ultima modificación</InputLabel>
          <Select
            labelId="days-controlled-open-select-label"
            id="days-controlled-open-select"
            value={fState.DaysBefore ?? 0}
            onChange={(evt) =>
              onChangeFilter('DaysBefore', (evt.target.value as number) || null)
            }
          >
            <MenuItem value={0}>Sin Filtrar</MenuItem>
            <MenuItem value={5}>5 dias</MenuItem>
            <MenuItem value={15}>15 dias</MenuItem>
            <MenuItem value={30}>30 dias</MenuItem>
          </Select>
        </FormControl>
        <FormGroup row style={{ marginRight: '10px', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={fState.Active === null}
                onChange={() => {
                  const updatedFilter = { ...fState, Active: null };
                  setFilter(updatedFilter);
                  dispatch(workshopAll({ params, filterN: updatedFilter }));
                }}
                color="primary"
              />
            }
            label="Todos"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={fState.Active === 'Active'}
                onChange={() => {
                  const updatedFilter = { ...fState, Active: 'Active' };
                  setFilter(updatedFilter);
                  dispatch(workshopAll({ params, filterN: updatedFilter }));
                }}
                color="primary"
              />
            }
            label="Activos"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={fState.Active === 'Disabled'}
                onChange={() => {
                  const updatedFilter = { ...fState, Active: 'Disabled' };
                  setFilter(updatedFilter);
                  dispatch(workshopAll({ params, filterN: updatedFilter }));
                }}
                color="primary"
              />
            }
            label="Inactivos"
          />
        </FormGroup>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => handleSearch(fState)}
            className={classes.submit}
            style={{ color: '#343c89', marginRight: '5px', height: 'fit-content' }}
            startIcon={<Search />}
            disabled={hasDateValidationError()}
          >
            Buscar
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleResetSearch()}
            className={classes.submit}
            style={{ color: '#343c89', marginRight: '5px', height: 'fit-content' }}
            startIcon={<Refresh />}
          >
            Refrescar
          </Button>
          <Button
            variant="outlined"
            style={{ color: '#343c89', marginRight: '5px', height: 'fit-content' }}
            startIcon={<GetAppRounded />}
            onClick={async () => {
              setLoading(true);
              await reportWorkshop(fState);
              setLoading(false);
            }}
            disabled={hasDateValidationError() || loading}
          >
            Descargar Reporte {loading && <CircularProgress size={20} />}
          </Button>
          {isCoordinator && <FilterReportMap />}
        </div>
      </div>
      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle>Importante!</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Para generar el reporte, debes seleccionar una fecha.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
